import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtPct, fmtNum, exportToPDF, exportToExcel } from "../lib/exports";
import { FileDown, FileSpreadsheet } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function ConsultaFecha() {
  const [fecha, setFecha] = useState(today());
  const [sucursalId, setSucursalId] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/sucursales").then((r) => setSucursales(r.data)); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { fecha };
      if (sucursalId) params.sucursal_id = sucursalId;
      const { data } = await api.get("/reportes/fecha", { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const r = data?.resumen || {};

  const expPDF = () => {
    if (!data) return;
    exportToPDF({
      title: `Reporte ${fecha}`,
      subtitle: sucursalId ? sucursales.find((s) => s.id === sucursalId)?.nombre : "Todas las sucursales",
      columns: [
        { header: "Producto", key: "nombre" }, { header: "Cant.", key: "cantidad" },
        { header: "Total", key: "total_fmt" }, { header: "Utilidad", key: "util_fmt" }, { header: "Margen %", key: "margen_fmt" },
      ],
      rows: data.ventas_por_producto.map((p) => ({ ...p, total_fmt: fmtMoney(p.total), util_fmt: fmtMoney(p.utilidad), margen_fmt: fmtPct(p.margen_pct) })),
      summary: [
        { label: "Ventas", value: fmtMoney(r.ventas) }, { label: "Costo", value: fmtMoney(r.costo_venta) },
        { label: "Gastos Op.", value: fmtMoney(r.gastos_operativos) }, { label: "Utilidad Neta", value: fmtMoney(r.utilidad_neta) },
        { label: "Food Cost", value: fmtPct(r.food_cost_pct) }, { label: "Margen", value: fmtPct(r.margen_pct) },
      ],
    });
  };
  const expXLS = () => {
    if (!data) return;
    exportToExcel({
      title: `Reporte_${fecha}`,
      rows: data.ventas_por_producto.map((p) => ({
        Producto: p.nombre, Cantidad: p.cantidad, Total: p.total, Costo: p.costo, Utilidad: p.utilidad, "Margen %": p.margen_pct,
      })),
    });
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Análisis</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Consulta por Fecha</h1>
          <p className="text-sm text-slate-500 mt-1">Estado financiero del día seleccionado.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Fecha"><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="consulta-fecha" className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Sucursal">
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-end"><Button onClick={cargar} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Consultar</Button></div>
      </Card>

      {loading && <div className="text-sm text-slate-500">Cargando…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Ventas Totales" value={fmtMoney(r.ventas)} />
            <KPI label="Gastos Totales" value={fmtMoney(r.gastos_totales)} />
            <KPI label="Utilidad Neta" value={fmtMoney(r.utilidad_neta)} positive={r.utilidad_neta >= 0} />
            <KPI label="Tickets" value={r.tickets || 0} />
            <KPI label="Food Cost %" value={fmtPct(r.food_cost_pct)} positive={r.food_cost_pct <= 40 ? true : r.food_cost_pct >= 50 ? false : undefined} />
            <KPI label="Margen Neto %" value={fmtPct(r.margen_pct)} positive={r.margen_pct >= 0} />
            <KPI label="Pollos Equivalentes" value={fmtNum(r.pollos_equivalentes, 1)} />
            <KPI label="Ganancia por Pollo" value={fmtMoney(r.ganancia_por_pollo)} positive={r.ganancia_por_pollo >= 0} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 bg-slate-900 text-white lg:col-span-1">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400 mb-3">Estado de Resultados</div>
              <div className="space-y-2 font-mono text-sm">
                <Row label="Ventas" value={fmtMoney(r.ventas)} />
                <Row label="(-) Costo de Venta" value={fmtMoney(r.costo_venta)} />
                <Row label="= Utilidad Bruta" value={fmtMoney(r.utilidad_bruta)} bold positive={r.utilidad_bruta >= 0} />
                <Row label="(-) Gastos Operativos" value={fmtMoney(r.gastos_operativos)} />
                <Row label="= Utilidad Neta" value={fmtMoney(r.utilidad_neta)} bold positive={r.utilidad_neta >= 0} />
              </div>
            </Card>

            <Card className="p-5 lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Ventas por Producto</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200">
                    <tr><th className="px-2 py-2 text-left">Producto</th><th className="px-2 py-2 text-right">Cant.</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Utilidad</th><th className="px-2 py-2 text-right">Margen</th></tr>
                  </thead>
                  <tbody>
                    {data.ventas_por_producto.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-400">Sin ventas</td></tr>}
                    {data.ventas_por_producto.map((p) => (
                      <tr key={p.producto_id} className="border-b border-slate-100">
                        <td className="px-2 py-1.5">{p.nombre}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtNum(p.cantidad, 0)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(p.total)}</td>
                        <td className={`px-2 py-1.5 text-right font-mono ${p.utilidad >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(p.utilidad)}</td>
                        <td className={`px-2 py-1.5 text-right font-mono ${p.margen_pct >= 55 ? "text-emerald-600" : p.margen_pct >= 45 ? "text-amber-600" : "text-red-600"}`}>{fmtPct(p.margen_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Gastos por Categoría</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.gastos_por_categoria.length === 0 && <div className="text-sm text-slate-400">Sin gastos registrados</div>}
              {data.gastos_por_categoria.map((c) => (
                <div key={c.categoria} className="border border-slate-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">{c.categoria}</div>
                  <div className="font-mono text-lg font-semibold mt-1">{fmtMoney(c.importe)}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const KPI = ({ label, value, positive }) => (
  <Card className="p-4">
    <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
    <div className={`mt-2 text-2xl font-bold font-mono tracking-tight ${positive === true ? "text-emerald-600" : positive === false ? "text-red-600" : ""}`}>{value}</div>
  </Card>
);
const Row = ({ label, value, bold, positive }) => (
  <div className="flex justify-between">
    <span className={`text-slate-300 ${bold ? "font-semibold text-white" : ""}`}>{label}</span>
    <span className={`${bold ? "font-bold text-lg" : ""} ${positive === true ? "text-emerald-400" : positive === false ? "text-red-400" : "text-white"}`}>{value}</span>
  </div>
);
